// SPDX-FileCopyrightText: 2026 Igor Brkic <igor@hyperglitch.com>
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Positional analysis: telling "this moved" apart from "this changed"
 * (spec §6.3: "`moved` = matched, and the only differences are positional").
 */

import { atoms, get, getAll, getNums, isNode, type Node, type SexpChild } from '../sexp/index.ts';
import { harvestPoints } from '../model/objects.ts';
import type { Point } from '../model/geometry.ts';

export const POSITION_TOKENS: ReadonlySet<string> = new Set(['at', 'start', 'end', 'mid', 'center', 'xy']);

/** Coordinates are stored in mm; 1 nm of slack absorbs float noise only. */
export const POSITION_TOLERANCE_MM = 1e-6;

function pt(n: Node, token: string): Point | null {
  const child = get(n, token);
  if (!child) return null;
  const nums = getNums(n, token);
  return nums.length >= 2 ? [nums[0]!, nums[1]!] : null;
}

/**
 * The point the UI draws a displacement arrow from. Chosen per type so that the
 * arrow starts somewhere meaningful rather than at an arbitrary vertex.
 */
export function anchorOf(n: Node): Point | null {
  switch (n.name) {
    case 'footprint':
    case 'via':
    case 'gr_text':
    case 'gr_text_box':
    case 'image':
    case 'target':
    case 'dimension':
    case 'barcode':
      return pt(n, 'at');
    case 'gr_circle':
      return pt(n, 'center') ?? pt(n, 'start');
    case 'segment':
    case 'arc':
    case 'gr_line':
    case 'gr_arc':
    case 'gr_rect':
    case 'gr_bbox':
      return pt(n, 'start');
    case 'zone':
    case 'gr_poly':
    case 'gr_curve': {
      const holder = n.name === 'zone' ? get(n, 'polygon') : n;
      const pts = holder ? get(holder, 'pts') : undefined;
      const first = pts ? getAll(pts, 'xy')[0] : undefined;
      if (first) {
        const nums = atoms(first).map((a) => Number(a.value)).filter((v) => Number.isFinite(v));
        if (nums.length >= 2) return [nums[0]!, nums[1]!];
      }
      return harvestPoints(n)[0] ?? null;
    }
    default:
      return pt(n, 'at') ?? pt(n, 'start') ?? harvestPoints(n)[0] ?? null;
  }
}

/** Rotation angle, where the token carries one. */
export function rotationOf(n: Node): number | undefined {
  const nums = getNums(n, 'at');
  return nums.length >= 3 ? nums[2] : undefined;
}

/**
 * Are `a` and `b` the same object translated by (dx, dy)?
 *
 * Footprint children live in the footprint's own coordinate frame, so moving a
 * footprint changes only its top-level `at`. Translating its pads too would
 * make every moved footprint look modified — hence the local-frame handling.
 */
export function translatedEqual(a: Node, b: Node, dx: number, dy: number, tol = POSITION_TOLERANCE_MM): boolean {
  return nodeEq(a, b, dx, dy, tol, true);
}

function nodeEq(a: Node, b: Node, dx: number, dy: number, tol: number, translate: boolean): boolean {
  if (a.name !== b.name) return false;
  if (a.children.length !== b.children.length) return false;

  const aa = atoms(a);
  const ba = atoms(b);
  if (aa.length !== ba.length) return false;

  if (translate && POSITION_TOKENS.has(a.name) && aa.length >= 2) {
    const ax = Number(aa[0]!.value);
    const ay = Number(aa[1]!.value);
    const bx = Number(ba[0]!.value);
    const by = Number(ba[1]!.value);
    if (!Number.isFinite(ax) || !Number.isFinite(ay) || !Number.isFinite(bx) || !Number.isFinite(by)) return false;
    if (Math.abs(ax + dx - bx) > tol || Math.abs(ay + dy - by) > tol) return false;
    for (let i = 2; i < aa.length; i++) {
      if (aa[i]!.value !== ba[i]!.value) return false;
    }
  } else {
    for (let i = 0; i < aa.length; i++) {
      if (aa[i]!.value !== ba[i]!.value) return false;
    }
  }

  // A footprint's own frame: only its direct `at` participates in the move.
  const localFrame = a.name === 'footprint';
  const aChildren = a.children.filter(isNode);
  const bChildren = b.children.filter(isNode);
  if (aChildren.length !== bChildren.length) return false;
  for (let i = 0; i < aChildren.length; i++) {
    const ca = aChildren[i]!;
    const cb = bChildren[i]!;
    const childTranslate = localFrame ? ca.name === 'at' : translate;
    if (!nodeEq(ca, cb, dx, dy, tol, childTranslate)) return false;
  }

  // Atom/node interleaving must also match, or the trees differ structurally.
  return sameShape(a.children, b.children);
}

function sameShape(a: readonly SexpChild[], b: readonly SexpChild[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i]!.kind !== b[i]!.kind) return false;
  }
  return true;
}

export interface PositionAnalysis {
  /** Anchor displacement, present whenever both anchors are known and differ. */
  displacement?: [number, number];
  /** True when B is exactly A translated by `displacement`. */
  translationOnly: boolean;
  /** Rotation change, if the token carries an angle. */
  rotation?: [number, number];
}

/**
 * Compare two *normalised* nodes positionally.
 *
 * Call this only after the trees are known to differ; if they are identical the
 * object is `unchanged` and there is nothing positional to report.
 */
export function analysePosition(a: Node, b: Node): PositionAnalysis {
  const result: PositionAnalysis = { translationOnly: false };

  const ra = rotationOf(a);
  const rb = rotationOf(b);
  if (ra !== undefined && rb !== undefined && ra !== rb) result.rotation = [ra, rb];

  const pa = anchorOf(a);
  const pb = anchorOf(b);
  if (pa === null || pb === null) return result;

  const dx = pb[0] - pa[0];
  const dy = pb[1] - pa[1];
  if (Math.abs(dx) > POSITION_TOLERANCE_MM || Math.abs(dy) > POSITION_TOLERANCE_MM) {
    result.displacement = [dx, dy];
  }
  result.translationOnly = result.rotation === undefined && translatedEqual(a, b, dx, dy);
  return result;
}
